import { useDropzone } from 'react-dropzone';
import { Box } from '@material-ui/core';
import clsx from 'clsx';

import { SButton, SETVIImage, SText } from '@setvi/shared/components';
import { UploadFileIcon } from '@setvi/shared/components/sicons';

import { MAX_COVER_LOGO_BYTES } from '../../constants';
import { useStyles } from './styles';

const ACCEPTED_TYPES: Record<string, string[]> = {
  'image/*': ['.png', '.jpg', '.jpeg', '.svg']
};

const MAX_MB = Math.round(MAX_COVER_LOGO_BYTES / (1024 * 1024));

interface CoverLogoProps {
  /** Logo to show: the company one, the rep's upload, or null when removed. */
  src: string | null;
  /** True while the company logo is in use (the rep has not changed it). */
  isCompanyLogo: boolean;
  /** Whether a company logo exists to fall back to. */
  hasCompanyLogo: boolean;
  disabled: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
  onReset: () => void;
  onError: (message: string) => void;
}

export const CoverLogo = ({
  src,
  isCompanyLogo,
  hasCompanyLogo,
  disabled,
  onSelect,
  onRemove,
  onReset,
  onError
}: CoverLogoProps) => {
  const classes = useStyles();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_TYPES,
    maxSize: MAX_COVER_LOGO_BYTES,
    multiple: false,
    disabled,
    // The filled state has its own buttons, so only the empty drop area opens
    // the picker on click.
    noClick: !!src,
    noKeyboard: !!src,
    onDropAccepted: files => onSelect(files[0]),
    onDropRejected: rejections => {
      const code = rejections[0]?.errors?.[0]?.code;
      if (code === 'file-too-large')
        onError(`Logo must be ${MAX_MB} MB or smaller.`);
      else onError('Choose a PNG, JPG or SVG image.');
    }
  });

  return (
    <Box>
      <SText title="Logo" size="sm" weight="semibold" />

      <Box
        mt={1}
        {...getRootProps()}
        className={clsx(classes.dropzone, {
          [classes.dropzoneEmpty]: !src,
          [classes.dropzoneActive]: isDragActive,
          [classes.dropzoneDisabled]: disabled
        })}>
        <input {...getInputProps()} />

        {src ? (
          <Box className={classes.filled}>
            <Box className={classes.thumb}>
              <SETVIImage
                src={src}
                alt="Cover logo"
                className={classes.thumbImage}
              />
            </Box>

            <Box className={classes.filledText}>
              <SText
                size="sm"
                weight="medium"
                title={isCompanyLogo ? 'Company logo' : 'Your logo'}
              />
              <SText
                size="xs"
                fontColor="#5D7083"
                maxLines="unset"
                title={
                  isDragActive
                    ? 'Drop to replace'
                    : 'Drag a new image here, or replace it below.'
                }
              />
              <Box className={classes.actions}>
                <SButton
                  variant="outlined"
                  color="primary"
                  className={classes.actionButton}
                  disabled={disabled}
                  onClick={open}>
                  Replace
                </SButton>
                <SButton
                  variant="text"
                  color="primary"
                  className={classes.actionTextButton}
                  disabled={disabled}
                  onClick={onRemove}>
                  Remove
                </SButton>
                {!isCompanyLogo && hasCompanyLogo && (
                  <SButton
                    variant="text"
                    color="primary"
                    className={classes.actionTextButton}
                    disabled={disabled}
                    onClick={onReset}>
                    Use company logo
                  </SButton>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box className={classes.empty}>
            <UploadFileIcon htmlColor="#1E96FC" fontSize="large" />
            <SText
              size="sm"
              weight="semibold"
              title={
                isDragActive ? 'Drop your logo here' : 'Drag & drop your logo'
              }
            />
            <SText
              size="xs"
              fontColor="#5D7083"
              title={`PNG, JPG or SVG, up to ${MAX_MB} MB`}
            />
            <Box className={classes.actions}>
              <SButton
                variant="outlined"
                color="primary"
                className={classes.actionButton}
                disabled={disabled}>
                Browse files
              </SButton>
              {hasCompanyLogo && (
                <SButton
                  variant="text"
                  color="primary"
                  className={classes.actionTextButton}
                  disabled={disabled}
                  onClick={event => {
                    // The empty area opens the picker on click — don't also
                    // open it when resetting back to the company logo.
                    event.stopPropagation();
                    onReset();
                  }}>
                  Use company logo
                </SButton>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
